/**
 * Global Admin → Admins → Operator Tokens (#1573).
 *
 * Superadmin-only minting of capability-scoped operator tokens — the
 * credential a headless tool authenticates with. Lives here,
 * behind the same `requireSuperadmin` gate as the rest of `/admin/*`,
 * rather than on the all-users Account → API tokens page: minting an
 * admin-tier credential is a trust-boundary operation.
 *
 * Reuses the Account → API tokens flow (list → form → one-time reveal →
 * revoke) — the shared `TokenReveal` is the same panel — but with the
 * workspace-scope picker replaced by a read-only statement of exactly
 * what the token grants, and a fixed expiry choice. A minted token is
 * owned by the minting superadmin and inert until the operator run
 * routes that accept it land (a follow-up slice).
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Loader2, ShieldCheck } from 'lucide-react'
import { Button, Input, Label } from '@/components/ui'
import { TokenReveal } from '@/components/TokenReveal'
import { AdminsSubNav } from './AdminsSubNav'
import { adminOperatorTokensAPI, type AdminOperatorTokenRow } from '@/lib/api'
import { toUserMessage } from '@/lib/userMessage'
import { useConfirm } from '@/hooks/useConfirm'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatDate } from '@/lib/utils'

const EXPIRY_OPTIONS: ReadonlyArray<{ label: string; days: number }> = [
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '1 year', days: 365 },
]

/** New-row highlight fade after Dismiss — same ~2s band as the Account
 * tokens page so the customer sees the confirmation without a toast. */
const NEW_ROW_HIGHLIGHT_MS = 2000

type View = 'list' | 'form' | 'shown'

export function OperatorTokensPage() {
  const confirm = useConfirm()
  const tz = useUserTimezone()
  const [tokens, setTokens] = useState<AdminOperatorTokenRow[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [view, setView] = useState<View>('list')
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [freshToken, setFreshToken] = useState<string | null>(null)
  const [newRowId, setNewRowId] = useState<string | null>(null)

  async function reload() {
    try {
      const rows = await adminOperatorTokensAPI.list()
      setTokens(rows)
      setLoadError(null)
    } catch (err) {
      setLoadError(toUserMessage(err, "Couldn't load automation tokens."))
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  async function handleRevoke(row: AdminOperatorTokenRow) {
    const ok = await confirm({
      title: 'Revoke this token?',
      description: (
        <>
          <strong>{row.name || '(unnamed)'}</strong> stops working immediately. Any
          tool still using it will start failing until you swap it for a fresh
          token.
        </>
      ),
      confirmLabel: 'Revoke token',
      destructive: true,
    })
    if (!ok) return
    setRevokingId(row.id)
    setActionError(null)
    try {
      await adminOperatorTokensAPI.revoke(row.id)
      await reload()
    } catch (err) {
      setActionError(
        toUserMessage(err, "Couldn't revoke that token. Try again — if the problem continues, refresh."),
      )
    } finally {
      setRevokingId(null)
    }
  }

  function handleDismiss() {
    setFreshToken(null)
    setView('list')
    const idToClear = newRowId
    setTimeout(() => {
      setNewRowId((current) => (current === idToClear ? null : current))
    }, NEW_ROW_HIGHLIGHT_MS)
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <AdminsSubNav />

      <div>
        <h3 className="text-base font-semibold">Automation tokens</h3>
        <p className="text-sm text-muted-foreground">
          Tokens for a tool outside the app that starts test runs and reads their
          results. That is all one can do. Tokens carry your access; keep them
          secret and revoke any you think has leaked.
        </p>
      </div>

      {view === 'shown' && freshToken && (
        <TokenReveal token={freshToken} onDismiss={handleDismiss} label="New automation token" />
      )}

      {view === 'list' && (
        <Button variant="outline" onClick={() => setView('form')}>
          Create automation token
        </Button>
      )}

      {view === 'form' && (
        <CreateTokenForm
          onCancel={() => setView('list')}
          onCreated={async (plaintext, newId) => {
            setFreshToken(plaintext)
            setNewRowId(newId)
            setView('shown')
            await reload()
          }}
        />
      )}

      {loadError && (
        <div className="space-y-2">
          <p className="text-sm text-destructive" role="alert">
            {loadError}
          </p>
          <Button variant="outline" onClick={reload}>
            Retry
          </Button>
        </div>
      )}
      {actionError && (
        <p className="text-sm text-destructive" role="alert">
          {actionError}
        </p>
      )}

      {view !== 'shown' && !loadError && tokens === null && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading…
        </div>
      )}
      {view !== 'shown' && !loadError && tokens !== null && tokens.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No automation tokens yet. Create one to let a tool outside the app start
          runs and read their results.
        </p>
      )}
      {view !== 'shown' && !loadError && tokens !== null && tokens.length > 0 && (
        <ul className="divide-y divide-border border rounded-md">
          {tokens.map((t) => {
            const isNew = newRowId !== null && t.id === newRowId
            return (
              <li
                key={t.id}
                data-testid={isNew ? 'new-token-row' : undefined}
                className={
                  'flex items-center justify-between py-3 px-3 gap-3 transition-colors duration-1000 ' +
                  (isNew ? 'bg-success/15' : '')
                }
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    {t.name || '(unnamed)'}
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary bg-primary/10 border border-primary/30 rounded px-1.5 py-0.5">
                      <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                      automation
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">Starts runs and reads results · nothing else</div>
                  {t.start && (
                    <div className="text-xs text-muted-foreground font-mono">{t.start}…</div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {t.createdAt && <>Created {formatDate(t.createdAt, tz)}</>}
                    {t.expiresAt && <> · Expires {formatDate(t.expiresAt, tz)}</>}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRevoke(t)}
                  disabled={revokingId !== null}
                >
                  {revokingId === t.id ? 'Revoking…' : 'Revoke'}
                </Button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function CreateTokenForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: (plaintext: string, id: string) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [expiresInDays, setExpiresInDays] = useState<number>(EXPIRY_OPTIONS[0]!.days)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError('Give the token a name so you can recognize it later.')
      return
    }
    setSubmitting(true)
    try {
      const created = await adminOperatorTokensAPI.create({ name: name.trim(), expiresInDays })
      await onCreated(created.key, created.id)
    } catch (err) {
      setError(
        toUserMessage(err, "Couldn't create the token. Try again — if the problem continues, check your connection."),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded border border-border p-4">
      <div className="space-y-2">
        <Label htmlFor="op-token-name">Name</Label>
        <Input
          id="op-token-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          placeholder="e.g. nightly-runs · laptop"
          autoFocus
          disabled={submitting}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="op-token-expiration">Expires after</Label>
        <select
          id="op-token-expiration"
          value={expiresInDays}
          onChange={(e) => setExpiresInDays(Number(e.target.value))}
          disabled={submitting}
          className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
        >
          {EXPIRY_OPTIONS.map((opt) => (
            <option key={opt.days} value={opt.days}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      {/* Fixed scope — operator tokens aren't workspace-scoped; this is
          a read-only statement of what the token grants, replacing the
          Account-tokens workspace picker. */}
      <div className="space-y-2">
        <Label>What this token can do</Label>
        <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
          <p className="text-foreground font-medium">Run tests from outside the app:</p>
          <p>• Start test runs with different models</p>
          <p>• Read the results of those runs</p>
          <p>Nothing else. It can't reach anything in your workspaces or other admin tools.</p>
        </div>
      </div>
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create token'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
